import React, { useMemo, useState } from 'react';
import { PlusIcon, MinusIcon, UserGroupIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../../contexts/LanguageContext';

interface RoleSetupStepProps {
  spaceData: any;
  setSpaceData: (data: any) => void;
  getStandardRevenueShare: (role: string) => number;
}

const RoleSetupStep: React.FC<RoleSetupStepProps> = ({ 
  spaceData, 
  setSpaceData, 
  getStandardRevenueShare 
}) => {
  const { language } = useLanguage();
  const [showRevenueSettings, setShowRevenueSettings] = useState(false);

  const availableRoles = useMemo(() => [
    language === 'zh' ? '作词' : 'Lyrics',
    language === 'zh' ? '作曲' : 'Composition',
    language === 'zh' ? '编曲' : 'Arrangement',
    language === 'zh' ? '演唱' : 'Vocals',
    language === 'zh' ? '混音' : 'Mixing',
    language === 'zh' ? '制作' : 'Production',
    language === 'zh' ? '投资人' : 'Investor', // 新增投资人角色
    language === 'zh' ? '其他' : 'Other'
  ], [language]);

  const handleRoleToggle = (role: string) => {
    setSpaceData((prev: any) => {
      const isRemoving = prev.selectedRoles.includes(role);
      if (isRemoving) {
        // Remove role and its assignments
        const newRoleAssignments = { ...prev.roleAssignments };
        delete newRoleAssignments[role];
        const newRevenueShares = { ...prev.revenueShares };
        delete newRevenueShares[role];
        
        // If revenue settings were shown and we're removing a role, keep them shown
        // but if all roles are removed, hide revenue settings
        const newSelectedRoles = prev.selectedRoles.filter((r: string) => r !== role);
        if (newSelectedRoles.length === 0) {
          setShowRevenueSettings(false);
        }
        
        return {
          ...prev,
          selectedRoles: newSelectedRoles,
          roleAssignments: newRoleAssignments,
          revenueShares: newRevenueShares
        };
      } else {
        // Add role and initialize with 1 person
        // Initialize earlySupporterProgram if it doesn't exist
        const newSpaceData = {
          ...prev,
          selectedRoles: [...prev.selectedRoles, role],
          roleAssignments: {
            ...prev.roleAssignments,
            [role]: { count: 1, members: [] }
          },
          revenueShares: {
            ...prev.revenueShares,
            [role]: getStandardRevenueShare(role)
          }
        };

        // Initialize earlySupporterProgram with default enabled if not exists
        if (!newSpaceData.earlySupporterProgram) {
          newSpaceData.earlySupporterProgram = {
            enabled: true,
            sharePercentage: 10
          };
        }

        return newSpaceData;
      }
    });
  };

  const updateRoleMemberCount = (role: string, count: number) => {
    setSpaceData((prev: any) => ({
      ...prev,
      roleAssignments: {
        ...prev.roleAssignments,
        [role]: { 
          ...prev.roleAssignments[role], 
          count: Math.max(1, Math.min(5, count)) 
        }
      }
    }));
  };

  const updateRevenueShare = (role: string, share: number) => {
    setSpaceData((prev: any) => ({
      ...prev,
      revenueShares: {
        ...prev.revenueShares,
        [role]: Math.max(0, Math.min(100, share))
      }
    }));
  };

  const getTotalPercentage = () => {
    return spaceData.selectedRoles.reduce((sum: number, role: string) => {
      return sum + (spaceData.revenueShares[role] || getStandardRevenueShare(role));
    }, 0);
  };

  const getCurrentRevenueShare = (role: string) => {
    return spaceData.revenueShares[role] || getStandardRevenueShare(role);
  };

  const getRoleDescription = (role: string) => {
    const descriptions: {[key: string]: string} = {
      [language === 'zh' ? '作词' : 'Lyrics']: language === 'zh' ? '负责歌词创作，表达情感和故事' : 'Responsible for lyric creation, expressing emotions and stories',
      [language === 'zh' ? '作曲' : 'Composition']: language === 'zh' ? '创作旋律和和弦进行' : 'Create melodies and chord progressions',
      [language === 'zh' ? '编曲' : 'Arrangement']: language === 'zh' ? '为歌曲添加乐器配置和层次' : 'Add instrument arrangements and layers to songs',
      [language === 'zh' ? '演唱' : 'Vocals']: language === 'zh' ? '主唱和和声演唱' : 'Lead and backing vocals',
      [language === 'zh' ? '混音' : 'Mixing']: language === 'zh' ? '平衡各轨道音量和音效处理' : 'Balance track volumes and audio effects processing',
      [language === 'zh' ? '制作' : 'Production']: language === 'zh' ? '整体制作和质量把控' : 'Overall production and quality control',
      [language === 'zh' ? '投资人' : 'Investor']: language === 'zh' ? '提供项目资金支持和商业推广' : 'Provide project funding and business promotion', // 新增投资人描述
      [language === 'zh' ? '其他' : 'Other']: language === 'zh' ? '其他特殊技能或贡献' : 'Other special skills or contributions'
    };
    return descriptions[role] || '';
  };

  return (
    <div className="space-y-8">
      {/* Step Title */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {language === 'zh' ? '设置所需角色' : 'Set Required Roles'}
        </h2>
        <p className="text-gray-600">
          {language === 'zh' ? '选择项目所需的角色和人数，并设置收益分成比例' : 'Select required roles and headcount for the project, and set revenue sharing ratios'}
        </p>
      </div>

      {/* Role Selection */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {language === 'zh' ? '选择需要的角色' : 'Select Required Roles'} <span className="text-red-500">*</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {availableRoles.map((role) => (
            <div 
              key={role} 
              className={`border-2 rounded-lg p-4 transition-all ${
                spaceData.selectedRoles.includes(role)
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={spaceData.selectedRoles.includes(role)}
                  onChange={() => handleRoleToggle(role)}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <h4 className="text-base font-medium text-gray-900 mb-1">
                    {role}
                  </h4>
                  <p className="text-sm text-gray-600 mb-3">
                    {getRoleDescription(role)}
                  </p>
                  
                  {spaceData.selectedRoles.includes(role) && (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <UserGroupIcon className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">
                          {language === 'zh' ? '人数:' : 'Count:'}
                        </span>
                        <div className="flex items-center border border-gray-300 rounded">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateRoleMemberCount(role, (spaceData.roleAssignments[role]?.count || 1) - 1);
                            }}
                            className="p-1 text-gray-600 hover:bg-gray-100 rounded-l"
                            disabled={(spaceData.roleAssignments[role]?.count || 1) <= 1}
                          >
                            <MinusIcon className="w-3 h-3" />
                          </button>
                          <span className="px-3 py-1 text-sm font-medium min-w-[2rem] text-center">
                            {spaceData.roleAssignments[role]?.count || 1}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateRoleMemberCount(role, (spaceData.roleAssignments[role]?.count || 1) + 1);
                            }}
                            className="p-1 text-gray-600 hover:bg-gray-100 rounded-r"
                            disabled={(spaceData.roleAssignments[role]?.count || 1) >= 5}
                          >
                            <PlusIcon className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">
                        {language === 'zh' ? '(最多5人)' : '(Max 5 people)'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Configure Revenue Button */}
      {spaceData.selectedRoles.length > 0 && !showRevenueSettings && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-2">
                {language === 'zh' ? '配置收益分成' : 'Configure Revenue Sharing'}
              </h3>
              <p className="text-blue-700 text-sm">
                {language === 'zh' 
                  ? `已选择 ${spaceData.selectedRoles.length} 个角色，点击下方按钮设置各角色的收益分成比例`
                  : `${spaceData.selectedRoles.length} roles selected. Click the button below to set revenue sharing ratios for each role`
                }
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowRevenueSettings(true)}
              className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <span>
                {language === 'zh' ? '设置分成比例' : 'Set Revenue Sharing'}
              </span>
              <ArrowRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Revenue Sharing */}
      {spaceData.selectedRoles.length > 0 && showRevenueSettings && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {language === 'zh' ? '设置分成比例' : 'Set Revenue Sharing'}
          </h3>
          <p className="text-sm text-gray-600 mb-6">
            {language === 'zh' 
              ? '调整各角色的收益分成比例，总计必须为100%。系统已根据行业标准预设了推荐比例。'
              : 'Adjust revenue sharing ratios for each role. Total must be 100%. System has preset recommended ratios based on industry standards.'
            }
          </p>
          
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <div className="space-y-6">
              {spaceData.selectedRoles.map((role: string) => {
                const currentShare = getCurrentRevenueShare(role);
                const standardShare = getStandardRevenueShare(role);
                return (
                  <div key={role} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-gray-700 font-medium flex-1 min-w-[80px]">
                          {role}
                        </span>
                        {currentShare !== standardShare && (
                          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                            {language === 'zh' ? '已调整' : 'Adjusted'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={currentShare}
                          onChange={(e) => updateRevenueShare(role, parseInt(e.target.value) || 0)}
                          className="w-20 px-3 py-2 text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <span className="text-gray-500 text-sm">%</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={currentShare}
                        onChange={(e) => updateRevenueShare(role, parseInt(e.target.value))}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${currentShare}%, #E5E7EB ${currentShare}%, #E5E7EB 100%)`
                        }}
                      />
                      <button
                        onClick={() => updateRevenueShare(role, standardShare)}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        {language === 'zh' ? '重置' : 'Reset'}
                      </button>
                    </div>
                  </div>
                );
              })}
              
              {/* Total */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-medium text-gray-900">
                    {language === 'zh' ? '总计' : 'Total'}
                  </span>
                  <span className={`text-2xl font-bold ${
                    getTotalPercentage() === 100 ? 'text-green-600' : 
                    getTotalPercentage() > 100 ? 'text-red-600' : 'text-yellow-600'
                  }`}>
                    {getTotalPercentage()}%
                  </span>
                </div>
                {getTotalPercentage() !== 100 && (
                  <div className={`mt-3 p-3 rounded-lg text-sm ${
                    getTotalPercentage() > 100 ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'
                  }`}>
                    {getTotalPercentage() > 100 
                      ? (language === 'zh' ? '⚠️ 分成比例超过100%，请调整各角色比例' : '⚠️ Revenue sharing exceeds 100%, please adjust role ratios')
                      : (language === 'zh' ? '⚠️ 分成比例不足100%，请调整各角色比例' : '⚠️ Revenue sharing is less than 100%, please adjust role ratios')
                    }
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Early Supporter Program - 只要显示了收益设置就显示，默认开启 */}
      {spaceData.selectedRoles.length > 0 && showRevenueSettings && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            
            <div className="flex-1">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium text-gray-900">
                  {language === 'zh' ? '🚀 先行者优势计划' : '🚀 Early Supporter Advantage Program'}
                </h3>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={spaceData.earlySupporterProgram?.enabled ?? true}
                    onChange={(e) => setSpaceData((prev: any) => ({
                      ...prev,
                      earlySupporterProgram: {
                        ...prev.earlySupporterProgram,
                        enabled: e.target.checked,
                        sharePercentage: prev.earlySupporterProgram?.sharePercentage || 10
                      }
                    }))}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 mr-2"
                  />
                  <span className="text-sm text-gray-600">
                    {language === 'zh' ? '启用计划' : 'Enable Program'}
                  </span>
                </div>
              </div>
              
              <p className="text-purple-700 text-sm mb-4">
                {language === 'zh' 
                  ? '回馈早期支持者！将作品收益的一部分分配给最先购买的粉丝，购买越早分成越高。这能激励粉丝早期支持，提升作品热度。'
                  : 'Reward early supporters! Allocate a portion of work revenue to fans who purchase first, with earlier purchases receiving higher shares. This incentivizes early support and boosts work popularity.'
                }
              </p>

              {/* 默认显示设置内容，因为默认开启 */}
              <div className="space-y-6 mt-6">
                {/* Share Percentage Setting */}
                <div className="bg-white rounded-lg p-4 border border-purple-100">
                  <h4 className="text-base font-medium text-gray-900 mb-3">
                    {language === 'zh' ? '设置回馈比例' : 'Set Reward Ratio'}
                  </h4>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {language === 'zh' ? '回馈比例:' : 'Reward Ratio:'}
                      </span>
                      <input
                        type="number"
                        min="3"
                        max="50"
                        value={spaceData.earlySupporterProgram?.sharePercentage || 10}
                        onChange={(e) => {
                          const value = Math.max(3, Math.min(50, parseInt(e.target.value) || 3));
                          setSpaceData((prev: any) => ({
                            ...prev,
                            earlySupporterProgram: {
                              ...prev.earlySupporterProgram,
                              enabled: prev.earlySupporterProgram?.enabled ?? true,
                              sharePercentage: value
                            }
                          }));
                        }}
                        className="w-20 px-3 py-2 text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        disabled={!(spaceData.earlySupporterProgram?.enabled ?? true)}
                      />
                      <span className="text-gray-500 text-sm">%</span>
                    </div>
                    <input
                      type="range"
                      min="3"
                      max="50"
                      value={spaceData.earlySupporterProgram?.sharePercentage || 10}
                      onChange={(e) => setSpaceData((prev: any) => ({
                        ...prev,
                        earlySupporterProgram: {
                          ...prev.earlySupporterProgram,
                          enabled: prev.earlySupporterProgram?.enabled ?? true,
                          sharePercentage: parseInt(e.target.value)
                        }
                      }))}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #8B5CF6 0%, #8B5CF6 ${((spaceData.earlySupporterProgram?.sharePercentage || 10) - 3) / 47 * 100}%, #E5E7EB ${((spaceData.earlySupporterProgram?.sharePercentage || 10) - 3) / 47 * 100}%, #E5E7EB 100%)`
                      }}
                      disabled={!(spaceData.earlySupporterProgram?.enabled ?? true)}
                    />
                    <span className="text-xs text-gray-500">3%-50%</span>
                  </div>
                  
                  <div className="mt-3 p-3 bg-purple-50 rounded-lg">
                    <p className="text-sm text-purple-700">
                      {language === 'zh' 
                        ? `您将从作品总收益中拿出 ${spaceData.earlySupporterProgram?.sharePercentage || 10}% 回馈给早期支持者。这部分将按购买顺序递减分配给前100位购买者。`
                        : `You will allocate ${spaceData.earlySupporterProgram?.sharePercentage || 10}% of total work revenue to reward early supporters. This portion will be distributed to the first 100 purchasers in decreasing order.`
                      }
                    </p>
                  </div>
                </div>

                {/* Distribution Example */}
                <div className="bg-white rounded-lg p-4 border border-purple-100">
                  <h4 className="text-base font-medium text-gray-900 mb-3">
                    {language === 'zh' ? '分配示例' : 'Distribution Example'}
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {(() => {
                      const currentSharePercentage = spaceData.earlySupporterProgram?.sharePercentage || 10;
                      // 动态计算基于实际设置比例的分配示例
                      const baseDistribution = [
                        { rank: 1, ratio: 0.20 },   // 第1名获得总回馈的20%
                        { rank: 2, ratio: 0.18 },   // 第2名获得总回馈的18%
                        { rank: 10, ratio: 0.10 },  // 第10名获得总回馈的10%
                        { rank: 50, ratio: 0.02 }   // 第50名获得总回馈的2%
                      ];
                      
                      return baseDistribution.map((item) => {
                        const actualPercentage = (currentSharePercentage * item.ratio).toFixed(1);
                        return (
                          <div key={item.rank} className="text-center p-3 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg">
                            <div className="text-lg font-bold text-purple-600">
                              #{item.rank}
                            </div>
                            <div className="text-sm text-gray-600">
                              {language === 'zh' ? '购买者' : 'Buyer'}
                            </div>
                            <div className="text-base font-medium text-gray-900 mt-1">
                              {actualPercentage}%
                            </div>
                            <div className="text-xs text-gray-500">
                              {language === 'zh' ? '获得分成' : 'Gets Share'}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    {language === 'zh' 
                      ? `* 示例基于${spaceData.earlySupporterProgram?.sharePercentage || 10}%回馈比例，按购买顺序递减分配`
                      : `* Example based on ${spaceData.earlySupporterProgram?.sharePercentage || 10}% reward ratio, distributed in decreasing order by purchase sequence`
                    }
                  </p>
                </div>

                {/* Program Rules */}
                <div className="bg-white rounded-lg p-4 border border-purple-100">
                  <h4 className="text-base font-medium text-gray-900 mb-3">
                    {language === 'zh' ? '计划规则' : 'Program Rules'}
                  </h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-start gap-2">
                      <span className="text-purple-500 font-bold">•</span>
                      <span>
                        {language === 'zh' 
                          ? '仅前100位购买者可享受先行者分成'
                          : 'Only the first 100 purchasers can enjoy early supporter rewards'
                        }
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-purple-500 font-bold">•</span>
                      <span>
                        {language === 'zh' 
                          ? '购买时间以区块链交易确认时间为准'
                          : 'Purchase time is based on blockchain transaction confirmation time'
                        }
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-purple-500 font-bold">•</span>
                      <span>
                        {language === 'zh' 
                          ? '分成将在作品收益产生后自动分配到钱包'
                          : 'Rewards will be automatically distributed to wallets after work revenue is generated'
                        }
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-purple-500 font-bold">•</span>
                      <span>
                        {language === 'zh' 
                          ? '计划一旦启动无法关闭，请谨慎设置'
                          : 'Once activated, the program cannot be disabled, please set carefully'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revenue Visualization */}
      {spaceData.selectedRoles.length > 0 && showRevenueSettings && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {language === 'zh' ? '收益分配可视化' : 'Revenue Distribution Visualization'}
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Pie Chart */}
            <div className="flex justify-center">
              <div className="relative w-64 h-64">
                <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                  {(() => {
                    const earlySupporterPercentage = (spaceData.earlySupporterProgram?.enabled ?? true) ? (spaceData.earlySupporterProgram?.sharePercentage || 10) : 0;
                    const creatorPercentage = 100 - earlySupporterPercentage;
                    let offset = 0;
                    const segments = [];
                    
                    // Creator roles segments
                    spaceData.selectedRoles.forEach((role: string, index: number) => {
                      const percentage = (getCurrentRevenueShare(role) / 100) * creatorPercentage;
                      const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316'];
                      const strokeDasharray = `${percentage} ${100 - percentage}`;
                      const strokeDashoffset = -offset;
                      
                      segments.push(
                        <circle
                          key={role}
                          cx="50"
                          cy="50"
                          r="15.915"
                          fill="transparent"
                          stroke={colors[index % colors.length]}
                          strokeWidth="4"
                          strokeDasharray={strokeDasharray}
                          strokeDashoffset={strokeDashoffset}
                          className="transition-all duration-300"
                        />
                      );
                      offset += percentage;
                    });
                    
                    // Early supporter segment
                    if (earlySupporterPercentage > 0) {
                      segments.push(
                        <circle
                          key="early-supporter"
                          cx="50"
                          cy="50"
                          r="15.915"
                          fill="transparent"
                          stroke="#8B5CF6"
                          strokeWidth="6"
                          strokeDasharray={`${earlySupporterPercentage} ${100 - earlySupporterPercentage}`}
                          strokeDashoffset={-offset}
                          className="transition-all duration-300"
                          opacity="0.8"
                        />
                      );
                    }
                    
                    return segments;
                  })()}
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      100%
                    </div>
                    <div className="text-sm text-gray-500">
                      {language === 'zh' ? '总收益' : 'Total Revenue'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Legend */}
            <div className="space-y-4">
              <h4 className="text-base font-medium text-gray-900">
                {language === 'zh' ? '分配详情' : 'Distribution Details'}
              </h4>
              <div className="space-y-3">
                {/* Creator Roles */}
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium text-gray-900">
                      {language === 'zh' ? '🎵 创作者收益' : '🎵 Creator Revenue'}
                    </h5>
                    <span className="text-lg font-bold text-blue-600">
                      {(spaceData.earlySupporterProgram?.enabled ?? true)
                        ? (100 - (spaceData.earlySupporterProgram?.sharePercentage || 10))
                        : 100
                      }%
                    </span>
                  </div>
                  {spaceData.selectedRoles.map((role: string, index: number) => {
                    const originalPercentage = getCurrentRevenueShare(role);
                    const adjustedPercentage = (spaceData.earlySupporterProgram?.enabled ?? true)
                      ? (originalPercentage / 100) * (100 - (spaceData.earlySupporterProgram?.sharePercentage || 10))
                      : originalPercentage;
                    const memberCount = spaceData.roleAssignments[role]?.count || 1;
                    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316'];
                    return (
                      <div key={role} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded"
                            style={{ backgroundColor: colors[index % colors.length] }}
                          ></div>
                          <div>
                            <span className="text-gray-900 text-sm font-medium">{role}</span>
                            <span className="text-gray-500 text-xs ml-1">
                              ({memberCount} {language === 'zh' ? '人' : 'people'})
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-gray-900 text-sm">
                            {adjustedPercentage.toFixed(1)}%
                          </div>
                          {memberCount > 1 && (
                            <div className="text-xs text-gray-500">
                              {(adjustedPercentage / memberCount).toFixed(1)}%/{language === 'zh' ? '人' : 'person'}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Early Supporter Program */}
                {(spaceData.earlySupporterProgram?.enabled ?? true) && (
                  <div className="border-2 border-purple-200 bg-purple-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium text-purple-900">
                        {language === 'zh' ? '🚀 先行者计划' : '🚀 Early Supporter Program'}
                      </h5>
                      <span className="text-lg font-bold text-purple-600">
                        {spaceData.earlySupporterProgram?.sharePercentage || 10}%
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-purple-700">
                      <div className="w-3 h-3 rounded bg-purple-500"></div>
                      <span>
                        {language === 'zh' 
                          ? '分配给前100位购买者'
                          : 'Distributed to first 100 purchasers'
                        }
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="text-center">
                  <div className="text-sm text-green-700 mb-1">
                    {language === 'zh' ? '分配方案已完成' : 'Distribution Plan Complete'}
                  </div>
                  <div className="text-lg font-bold text-green-800">
                    ✅ {language === 'zh' ? '100% 收益分配完毕' : '100% Revenue Allocated'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleSetupStep;